import { useState, useMemo, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import { api, apiPost, apiPut, apiDelete } from '../lib/api'
import { useTabParam } from '../hooks/useTabParam'
import AdminClassificationsPage from './AdminClassificationsPage'
import { ConfirmDialog } from '../components/ConfirmDialog'
import '../styles/role-admin.css'
import '../styles/ui.css'

type PermDef = { key: string; label: string; category: string; storeScoped: boolean }
type Role = {
    id: string; name: string; description: string | null; key: string | null
    isSystem: boolean; isSuperAdmin: boolean; rank: number; sortOrder: number
    permissions: string[]; userCount: number
}
type RolesResponse = { roles: Role[]; catalog: PermDef[]; adminOnly: string[] }
type RoleUser = { id: string; displayName: string; email: string; avatarUrl: string | null; isActive: boolean }

const userInitials = (s: string) =>
    s.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'

const TABS = ['roles', 'classifications'] as const
type Tab = typeof TABS[number]

const setEqual = (a: Set<string>, b: Set<string>) => a.size === b.size && [...a].every(x => b.has(x))

// A guard function: given a "proceed" callback, either run it now (clean) or prompt first (dirty).
type LeaveGuard = (proceed: () => void) => void

export default function RoleAdminPage() {
    const [tab, setTab] = useTabParam<Tab>(TABS, 'roles')
    // RolesTab registers its unsaved-changes guard here so tab switches can be intercepted too.
    const guardRef = useRef<LeaveGuard>((proceed) => proceed())
    const tryLeave: LeaveGuard = (proceed) => guardRef.current(proceed)

    return (
        <div className="page">
            <div className="page-header">
                <h1>🛡 Roles &amp; permissions</h1>
                <p style={{ color: 'var(--color-text-secondary)', marginTop: 4 }}>
                    Grant each role a specific set of permissions. SuperAdmin always has full access.
                </p>
            </div>

            <div className="admin-subtabs">
                <button className={`admin-subtab${tab === 'roles' ? ' active' : ''}`}
                    onClick={() => tryLeave(() => setTab('roles'))}>Roles</button>
                <button className={`admin-subtab${tab === 'classifications' ? ' active' : ''}`}
                    onClick={() => tryLeave(() => setTab('classifications'))}>Staff classifications</button>
            </div>

            {tab === 'roles'
                ? <RolesTab registerGuard={(fn) => { guardRef.current = fn }} />
                : <AdminClassificationsPage />}
        </div>
    )
}

function RolesTab({ registerGuard }: { registerGuard: (guard: LeaveGuard) => void }) {
    const token = useAuthStore(s => s.accessToken)
    const qc = useQueryClient()
    const [params, setParams] = useSearchParams()
    const [creating, setCreating] = useState(false)
    // Pending navigation awaiting an unsaved-changes decision.
    const [guard, setGuard] = useState<{ proceed: () => void } | null>(null)
    const [confirmDelete, setConfirmDelete] = useState(false)

    const { data, isLoading } = useQuery<RolesResponse>({
        queryKey: ['admin-roles'],
        queryFn: () => api('/api/admin/roles', token),
    })
    const roles = useMemo(() => data?.roles ?? [], [data])
    const adminOnly = useMemo(() => new Set(data?.adminOnly ?? []), [data])

    const selectedId = params.get('role')
    const selected = roles.find(r => r.id === selectedId) ?? null

    // ── Editing state (lifted so the guard can save on the user's behalf) ──
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [perms, setPerms] = useState<Set<string>>(new Set())

    const original = useMemo(() => creating
        ? { name: '', description: '', perms: new Set<string>() }
        : selected ? { name: selected.name, description: selected.description ?? '', perms: new Set(selected.permissions) } : null,
        [creating, selected])
    const locked = selected?.isSuperAdmin ?? false        // SuperAdmin isn't editable
    const nameLocked = !creating && (selected?.isSystem ?? false)

    // Seed the form when the selection (or create mode) changes — but only once the role's data
    // is actually available. Keying on `original` alone would clobber in-progress edits on every
    // background refetch, so we track which selection we've already seeded. This also fixes a
    // deep-link/hard-reload race where the role was selected via ?role= before data loaded, leaving
    // the fields blank (and the editor falsely "dirty").
    const seededFor = useRef<string | null>(null)
    useEffect(() => {
        const key = creating ? '__create__' : selectedId
        if (!creating && (!selectedId || !original)) return // wait for the selected role's data
        if (seededFor.current === key) return                // already seeded this selection
        seededFor.current = key
        setName(original?.name ?? '')
        setDescription(original?.description ?? '')
        setPerms(new Set(original?.perms ?? []))
    }, [selectedId, creating, original])

    const dirty = !!original && !locked && (
        name !== original.name || description !== original.description || !setEqual(perms, original.perms)
    )

    // Persist the current form. Returns a promise so the guard can save-then-proceed.
    const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-roles'] })
    const saveMut = useMutation({
        mutationFn: (p: { id: string; body: any }) => apiPut(`/api/admin/roles/${p.id}`, token, p.body),
        onSuccess: invalidate,
    })
    const createMut = useMutation({
        mutationFn: (body: any) => apiPost('/api/admin/roles', token, body),
        onSuccess: (res: any) => { invalidate(); if (res?.id) setRoleParam(res.id); setCreating(false) },
    })
    const deleteMut = useMutation({
        mutationFn: (id: string) => apiDelete(`/api/admin/roles/${id}`, token),
        onSuccess: () => { invalidate(); clearRoleParam() },
    })
    const saving = saveMut.isPending || createMut.isPending

    // ── Drag-and-drop reordering of the roles list ──────────────────────
    const [draggingId, setDraggingId] = useState<string | null>(null)
    const [dragOverId, setDragOverId] = useState<string | null>(null)
    const reorderMut = useMutation({
        mutationFn: (ids: string[]) => apiPut('/api/admin/roles/reorder', token, { ids }),
        onSuccess: invalidate,
    })
    const handleReorder = (dragId: string, targetId: string) => {
        setDraggingId(null); setDragOverId(null)
        if (!dragId || dragId === targetId) return
        const ordered = [...roles]
        const from = ordered.findIndex(r => r.id === dragId)
        const to = ordered.findIndex(r => r.id === targetId)
        if (from < 0 || to < 0) return
        const [moved] = ordered.splice(from, 1)
        ordered.splice(to, 0, moved)
        // Reflect the new order immediately, then persist it.
        qc.setQueryData<RolesResponse>(['admin-roles'], old => old ? { ...old, roles: ordered } : old)
        reorderMut.mutate(ordered.map(r => r.id))
    }

    // ── Users in the selected role + drag-to-reassign ───────────────────
    const { data: roleUsers, isLoading: usersLoading } = useQuery<RoleUser[]>({
        queryKey: ['role-users', selectedId],
        enabled: !!selectedId && !creating,
        queryFn: () => api(`/api/admin/roles/${selectedId}/users`, token),
    })
    const [draggingUserId, setDraggingUserId] = useState<string | null>(null)
    const [userDropRoleId, setUserDropRoleId] = useState<string | null>(null)
    const reassignMut = useMutation({
        mutationFn: (p: { userId: string; roleId: string }) => apiPut(`/api/admin/roles/${p.roleId}/users/${p.userId}`, token, {}),
        onSettled: () => {
            qc.invalidateQueries({ queryKey: ['admin-roles'] })
            qc.invalidateQueries({ queryKey: ['role-users'] })
        },
    })
    const reassignUser = (userId: string, roleId: string) => {
        setDraggingUserId(null); setUserDropRoleId(null)
        if (!userId || !roleId || roleId === selectedId) return
        // Optimistic: pull the user out of the current list and shift the two roles' counts.
        qc.setQueryData<RoleUser[]>(['role-users', selectedId], old => old?.filter(u => u.id !== userId))
        qc.setQueryData<RolesResponse>(['admin-roles'], old => old ? {
            ...old,
            roles: old.roles.map(r =>
                r.id === selectedId ? { ...r, userCount: Math.max(0, r.userCount - 1) }
                    : r.id === roleId ? { ...r, userCount: r.userCount + 1 } : r),
        } : old)
        reassignMut.mutate({ userId, roleId })
    }

    const buildBody = () => ({ name: name.trim(), description: description.trim(), permissions: [...perms] })
    const doSave = async () => {
        if (creating) await createMut.mutateAsync(buildBody())
        else if (selected) await saveMut.mutateAsync({ id: selected.id, body: buildBody() })
    }

    // ── URL-backed selection ────────────────────────────────────────────
    const setRoleParam = (id: string) => {
        const next = new URLSearchParams(params); next.set('role', id); setParams(next, { replace: true })
    }
    const clearRoleParam = () => {
        const next = new URLSearchParams(params); next.delete('role'); setParams(next, { replace: true })
    }
    // Auto-select the first role when none/invalid is selected (initial load, not guarded).
    useEffect(() => {
        if (creating) return
        if (roles.length && !roles.some(r => r.id === selectedId)) setRoleParam(roles[0].id)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roles, selectedId, creating])

    // ── Unsaved-changes guard ───────────────────────────────────────────
    const guardedNav: LeaveGuard = (proceed) => { dirty ? setGuard({ proceed }) : proceed() }
    // Expose the guard to the parent (for tab switches) and keep it fresh as `dirty` changes.
    useEffect(() => {
        registerGuard(guardedNav)
        return () => registerGuard((p) => p())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dirty])
    // Warn on refresh / tab close / hard navigation while dirty.
    useEffect(() => {
        if (!dirty) return
        const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
        window.addEventListener('beforeunload', h)
        return () => window.removeEventListener('beforeunload', h)
    }, [dirty])

    const selectRole = (id: string) => {
        if (id === selectedId && !creating) return // already viewing this role
        guardedNav(() => { setCreating(false); setRoleParam(id) })
    }
    const startCreate = () => guardedNav(() => setCreating(true))
    const togglePerm = (key: string) => {
        if (locked) return
        setPerms(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n })
    }

    const categories = useMemo(() => {
        const map = new Map<string, PermDef[]>()
        ;(data?.catalog ?? []).forEach(p => {
            if (!map.has(p.category)) map.set(p.category, [])
            map.get(p.category)!.push(p)
        })
        return [...map.entries()]
    }, [data])

    if (isLoading) return <div className="chart-panel"><p>Loading…</p></div>

    return (
        <div className="role-admin-grid">
            <div className="chart-panel role-list">
                <button className="btn btn-primary" style={{ width: '100%', marginBottom: 'var(--space-3)' }}
                    onClick={startCreate}>＋ New role</button>
                {roles.map(r => (
                    <button key={r.id}
                        className={`role-list-item${!creating && selectedId === r.id ? ' active' : ''}${draggingId === r.id ? ' dragging' : ''}${dragOverId === r.id && draggingId !== r.id ? ' drag-over' : ''}${userDropRoleId === r.id ? ' user-drop' : ''}`}
                        onClick={() => selectRole(r.id)}
                        draggable
                        onDragStart={() => setDraggingId(r.id)}
                        onDragOver={e => {
                            // Accept both a role being reordered and a user chip being reassigned.
                            if (draggingUserId) { e.preventDefault(); if (r.id !== selectedId) setUserDropRoleId(r.id); return }
                            e.preventDefault(); if (draggingId) setDragOverId(r.id)
                        }}
                        onDragLeave={() => {
                            setDragOverId(cur => (cur === r.id ? null : cur))
                            setUserDropRoleId(cur => (cur === r.id ? null : cur))
                        }}
                        onDrop={e => {
                            e.preventDefault()
                            if (draggingUserId) { reassignUser(draggingUserId, r.id); return }
                            if (draggingId) handleReorder(draggingId, r.id)
                        }}
                        onDragEnd={() => { setDraggingId(null); setDragOverId(null) }}>
                        <span className="role-drag-handle" aria-hidden="true">⠿</span>
                        <span className="role-list-name">{r.name}</span>
                        {r.isSuperAdmin && <span className="role-tag full">full</span>}
                        {!r.isSystem && <span className="role-tag custom">custom</span>}
                        <span className="role-count" title="Users with this role">{r.userCount}</span>
                    </button>
                ))}
            </div>

            <div className="chart-panel">
                {creating || selected ? (
                    <RoleEditor
                        mode={creating ? 'create' : 'edit'}
                        name={name} description={description} perms={perms}
                        onName={setName} onDescription={setDescription} onTogglePerm={togglePerm}
                        catalog={categories} adminOnly={adminOnly}
                        locked={locked} nameLocked={nameLocked} dirty={dirty} saving={saving}
                        onSave={doSave}
                        onCancel={creating ? () => setCreating(false) : undefined}
                        onDelete={!creating && selected && !selected.isSystem
                            ? () => setConfirmDelete(true)
                            : undefined}
                    />
                ) : <p style={{ color: 'var(--color-text-secondary)' }}>Select a role to edit its permissions.</p>}
            </div>

            {!creating && selected && (
                <div className="chart-panel role-users-panel">
                    <div className="role-users-head">
                        <h3>Users in “{selected.name}”</h3>
                        <span className="role-users-hint">Drag a user onto a role on the left to reassign them.</span>
                    </div>
                    {usersLoading ? (
                        <p className="role-users-empty">Loading…</p>
                    ) : !roleUsers || roleUsers.length === 0 ? (
                        <p className="role-users-empty">No users have this role yet.</p>
                    ) : (
                        <div className="role-users-list">
                            {roleUsers.map(u => (
                                <div key={u.id}
                                    className={`role-user-chip${draggingUserId === u.id ? ' dragging' : ''}${u.isActive ? '' : ' inactive'}`}
                                    draggable
                                    onDragStart={e => { setDraggingUserId(u.id); e.dataTransfer.effectAllowed = 'move' }}
                                    onDragEnd={() => { setDraggingUserId(null); setUserDropRoleId(null) }}
                                    title={`${u.email}${u.isActive ? '' : ' · inactive'} — drag onto a role to reassign`}>
                                    <span className="role-user-avatar">{userInitials(u.displayName || u.email)}</span>
                                    <span className="role-user-info">
                                        <span className="role-user-name">{u.displayName || u.email}</span>
                                        <span className="role-user-email">{u.email}</span>
                                    </span>
                                    {!u.isActive && <span className="role-user-inactive">inactive</span>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {guard && (
                <div className="modal-backdrop">
                    <div className="modal" style={{ maxWidth: 460 }}>
                        <div className="modal-header"><h2>Unsaved changes</h2></div>
                        <div className="modal-body">
                            <p style={{ margin: 0 }}>You have unsaved changes to this role. Save them before leaving?</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setGuard(null)}>Cancel</button>
                            <button className="btn btn-secondary" style={{ marginRight: 'auto' }}
                                onClick={() => { const p = guard.proceed; setGuard(null); p() }}>Discard changes</button>
                            <button className="btn btn-primary" disabled={saving}
                                onClick={async () => {
                                    try { await doSave() } catch { return } // save failed — stay put
                                    const p = guard.proceed; setGuard(null); p()
                                }}>
                                {saving ? 'Saving…' : 'Save changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirmDelete && selected && (
                <ConfirmDialog
                    title="Delete role?"
                    message={`Delete the "${selected.name}" role?`}
                    confirmLabel="Delete"
                    danger
                    onCancel={() => setConfirmDelete(false)}
                    onConfirm={() => { deleteMut.mutate(selected.id); setConfirmDelete(false) }}
                />
            )}
        </div>
    )
}

function RoleEditor({ mode, name, description, perms, onName, onDescription, onTogglePerm,
    catalog, adminOnly, locked, nameLocked, dirty, saving, onSave, onCancel, onDelete }: {
    mode: 'create' | 'edit'
    name: string; description: string; perms: Set<string>
    onName: (v: string) => void; onDescription: (v: string) => void; onTogglePerm: (key: string) => void
    catalog: [string, PermDef[]][]
    adminOnly: Set<string>
    locked: boolean; nameLocked: boolean; dirty: boolean; saving: boolean
    onSave: () => void
    onCancel?: () => void
    onDelete?: () => void
}) {
    return (
        <div>
            <div className="role-editor-head">
                <div className="form-field" style={{ flex: 1, margin: 0 }}>
                    <label className="form-label">Role name</label>
                    <input className="form-input" value={name} onChange={e => onName(e.target.value)}
                        disabled={nameLocked} placeholder="e.g. Regional Manager" />
                </div>
                <div className="form-field" style={{ flex: 2, margin: 0 }}>
                    <label className="form-label">Description</label>
                    <input className="form-input" value={description} onChange={e => onDescription(e.target.value)} />
                </div>
            </div>

            {locked && (
                <div className="role-locked-banner">SuperAdmin always has full access to every permission and can't be edited.</div>
            )}

            <div className="role-matrix">
                {catalog.map(([cat, defs]) => (
                    <div key={cat} className="role-matrix-group">
                        <div className="role-matrix-cat">{cat}</div>
                        {defs.map(d => (
                            <label key={d.key} className="role-perm">
                                <input type="checkbox" checked={locked || perms.has(d.key)}
                                    disabled={locked} onChange={() => onTogglePerm(d.key)} />
                                <span className="role-perm-label">{d.label}</span>
                                {adminOnly.has(d.key) && <span className="role-perm-admin">admin</span>}
                                {d.storeScoped && <span className="role-perm-scope">store</span>}
                            </label>
                        ))}
                    </div>
                ))}
            </div>

            <div className="role-editor-actions">
                {onDelete && <button className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={onDelete}>Delete role</button>}
                {onCancel && <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>}
                {!locked && (
                    <button className="btn btn-primary" disabled={saving || !dirty || !name.trim()} onClick={onSave}>
                        {saving ? 'Saving…' : mode === 'create' ? 'Create role' : 'Save changes'}
                    </button>
                )}
            </div>
        </div>
    )
}
